import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, MapPin } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, mediaUrl } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import ImagePicker from "@/components/admin/ImagePicker";

const EMPTY = { name: "", address: "", hours: "", phone: "", website: "", image: "", info: "", active: true, order: 0 };

export default function PosAdmin() {
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/points-of-sale").then((r) => setItems(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...editing, order: Number(editing.order) || 0 };
      if (editing.id) await api.put(`/admin/points-of-sale/${editing.id}`, payload);
      else await api.post("/admin/points-of-sale", payload);
      toast.success("Point de vente enregistré");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce point de vente ?")) return;
    await api.delete(`/admin/points-of-sale/${id}`);
    toast.success("Supprimé");
    load();
  };

  const toggle = async (item) => {
    await api.put(`/admin/points-of-sale/${item.id}`, { ...item, active: !item.active });
    load();
  };

  return (
    <div data-testid="admin-pos">
      <div className="flex items-center justify-between mb-8 gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight uppercase text-onyx">Points de vente</h1>
          <p className="text-sm text-muted-foreground mt-1">Revendeurs où le jeu est en rayon (≠ points de retrait).</p>
        </div>
        <button onClick={() => setEditing({ ...EMPTY })} data-testid="pos-add"
          className="inline-flex items-center gap-2 bg-rouge text-white px-5 py-2.5 text-sm font-bold uppercase tracking-wide hover:bg-rouge-hover transition-colors">
          <Plus className="w-4 h-4" /> Ajouter
        </button>
      </div>

      {!items ? <p className="text-muted-foreground">Chargement…</p> : (
        <div className="space-y-3" data-testid="pos-list">
          {items.map((p) => (
            <div key={p.id} className="bg-white border border-line p-5 flex items-center gap-4 flex-wrap" data-testid={`pos-row-${p.id}`}>
              {p.image ? <img src={mediaUrl(p.image)} alt="" className="w-10 h-10 object-contain" /> : <MapPin className="w-5 h-5 text-rouge shrink-0" />}
              <div className="flex-1 min-w-[200px]">
                <p className="font-display font-bold text-onyx">{p.name}</p>
                <p className="text-sm text-muted-foreground">{p.address || "Adresse à compléter"}</p>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={!!p.active} onCheckedChange={() => toggle(p)} data-testid={`pos-active-${p.id}`} />
                <span className="text-xs text-muted-foreground">{p.active ? "Actif" : "Inactif"}</span>
              </div>
              <button onClick={() => setEditing({ ...p })} data-testid={`pos-edit-${p.id}`} className="p-2 text-onyx hover:text-rouge" aria-label="Modifier"><Pencil className="w-4 h-4" /></button>
              <button onClick={() => remove(p.id)} data-testid={`pos-delete-${p.id}`} className="p-2 text-onyx hover:text-destructive" aria-label="Supprimer"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-white max-h-[90vh] overflow-y-auto" data-testid="pos-dialog">
          <DialogHeader><DialogTitle className="font-display">{editing?.id ? "Modifier" : "Ajouter"} un point de vente</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div><Label>Nom *</Label><Input data-testid="pos-name-input" className="mt-1.5" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} /></div>
              <div><Label>Adresse</Label><Input data-testid="pos-address-input" className="mt-1.5" value={editing.address} onChange={(e) => setEditing({ ...editing, address: e.target.value })} /></div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><Label>Horaires</Label><Input data-testid="pos-hours-input" className="mt-1.5" value={editing.hours} onChange={(e) => setEditing({ ...editing, hours: e.target.value })} /></div>
                <div><Label>Téléphone</Label><Input data-testid="pos-phone-input" className="mt-1.5" value={editing.phone} onChange={(e) => setEditing({ ...editing, phone: e.target.value })} /></div>
              </div>
              <div><Label>Site web</Label><Input data-testid="pos-website-input" className="mt-1.5" placeholder="https://…" value={editing.website} onChange={(e) => setEditing({ ...editing, website: e.target.value })} /></div>
              <ImagePicker label="Image / logo" value={editing.image} onChange={(url) => setEditing({ ...editing, image: url })} />
              <div><Label>Informations pratiques</Label><Input data-testid="pos-info-input" className="mt-1.5" value={editing.info} onChange={(e) => setEditing({ ...editing, info: e.target.value })} /></div>
              <div><Label>Ordre d'affichage</Label><Input type="number" data-testid="pos-order-input" className="mt-1.5" value={editing.order} onChange={(e) => setEditing({ ...editing, order: e.target.value })} /></div>
              <div className="flex items-center gap-3">
                <Switch data-testid="pos-active-switch" checked={!!editing.active} onCheckedChange={(v) => setEditing({ ...editing, active: v })} />
                <Label>Actif</Label>
              </div>
              <button onClick={save} disabled={saving || !editing.name} data-testid="pos-save"
                className="w-full bg-rouge text-white px-6 py-3 text-sm font-bold uppercase tracking-wide hover:bg-rouge-hover transition-colors disabled:opacity-50">
                {saving ? "Enregistrement…" : "Enregistrer"}
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
